import * as XLSX from 'xlsx-js-style';
import api from '../../../auth/apiClient';
import { isAxiosError } from 'axios';
import { trackDownload } from '../../../activity';

interface MutuAncakData {
    users_estate: string;
    estate: string;
    job_title: string | null;
    nip: string;
    name: string;
    division: string | null;
    block: string;
    spv_name: string | null;
    job_level: string | null;
    penalty_code: string | null;
    penalty_descr: string | null;
    qty: number | null;
    inspection_date: string | null;
    no_baris: string | null;
    no_pokok: string | null;
    ins_description: string | null;
    foreman_name: string | null;
    kerani_harvest_name: string | null;
    foreman1_name: string | null;
    asisten_name: string | null;
    asisten_kepala_name: string | null;
    estate_manager: string | null;
}

interface MutuBuahData {
    users_estate: string;
    estate: string;
    job_title: string | null;
    nip: string;
    name: string;
    division: string | null;
    no_potong: number | null;
    inspection_date_mb: string | null;
    spv_name_mb: string | null;
    job_level_mb: string | null;
    block_mb: string | null;
    tph_code: string | null;
    rotten_qty: number | null;
    unripe_qty: number | null;
    lstalk_qty: number | null;
    mb_description: string | null;
    foreman_name: string | null;
    kerani_harvest_name: string | null;
    foreman1_name: string | null;
    asisten_name: string | null;
    asisten_kepala_name: string | null;
    estate_manager: string | null;
}

interface ApiResponse {
    mutu_ancak: MutuAncakData[];
    mutu_buah: MutuBuahData[];
}

interface ReportSpvMbProps {
    selectedMonth: string;
    selectedEstate: number | null;
    setIsLoading: (loading: boolean) => void;
    months: Array<{ value: string; label: string }>;
    toast: any;
}

const ReportMtAncakMtBuah = {
    export: async ({ selectedMonth, selectedEstate, setIsLoading, months, toast }: ReportSpvMbProps) => {
        try {
            const params: any = { month: selectedMonth };
            if (selectedEstate) {
                params.estate_id = selectedEstate;
            }

            const response = await api.get<ApiResponse>(
                '/api/inspection/report-mt-ancak-mb',
                { params }
            );

            if (!response.data || (!response.data.mutu_ancak?.length && !response.data.mutu_buah?.length)) {
                toast({
                    title: 'Info',
                    description: 'Tidak ada data untuk dilaporkan pada periode terpilih.',
                    status: 'info',
                    duration: 3000,
                    isClosable: true,
                });
                setIsLoading(false);
                return;
            }

            const mutuAncakData = response.data.mutu_ancak || [];
            const mutuBuahData = response.data.mutu_buah || [];

            // Combine both datasets and group by estate
            const combinedData = [...mutuAncakData, ...mutuBuahData];
            const groupedByEstate = combinedData.reduce((acc, item) => {
                const estateKey = item.estate || 'UNDEFINED_ESTATE';
                if (!acc[estateKey]) {
                    acc[estateKey] = { mutu_ancak: [], mutu_buah: [] };
                }
                return acc;
            }, {} as Record<string, { mutu_ancak: MutuAncakData[], mutu_buah: MutuBuahData[] }>);

            // Populate the grouped data
            mutuAncakData.forEach(item => {
                const estateKey = item.estate || 'UNDEFINED_ESTATE';
                if (!groupedByEstate[estateKey]) {
                    groupedByEstate[estateKey] = { mutu_ancak: [], mutu_buah: [] };
                }
                groupedByEstate[estateKey].mutu_ancak.push(item);
            });

            mutuBuahData.forEach(item => {
                const estateKey = item.estate || 'UNDEFINED_ESTATE';
                if (!groupedByEstate[estateKey]) {
                    groupedByEstate[estateKey] = { mutu_ancak: [], mutu_buah: [] };
                }
                groupedByEstate[estateKey].mutu_buah.push(item);
            });

            const wb = XLSX.utils.book_new();

            const mutuAncakColumnOrder = [
                'TANGGAL INSPEKSI',
                'DIVISI',
                'BLOK',
                'NAMA SUPERVISOR',
                'LEVEL SUPERVISOR',
                'JENIS INSPEKSI',
                'NOMOR POTONG',
                'NAMA PEMANEN',
                'NIP',
                'JABATAN',
                'MANDOR PANEN',
                'KERANI PANEN',
                'MANDOR 1',
                'ASISTEN',
                'ASISTEN KEPALA',
                'MANAGER',
                'KODE PENALTY',
                'DESKRIPSI PENALTY',
                'NO BARIS',
                'NO POKOK',
                'QTY',
                'KETERANGAN TEMUAN MUTU ANCAK',
            ];

            const mutuBuahColumnOrder = [
                'NO TPH',
                'MENTAH (QTY - JJG)',
                'BUSUK/TANGKOS (QTY - JJG)',
                'TANGKAI PANJANG (QTY - JJG)',
                'KETERANGAN TEMUAN MUTU BUAH',
            ];

            const columnOrder = [...mutuAncakColumnOrder, ...mutuBuahColumnOrder];

            const generalHeaderStyle = {
                font: { bold: true, color: { rgb: "FFFFFF" } },
                fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "4A5A33" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            const mutuAncakHeaderStyle = {
                font: { bold: true, color: { rgb: "FFFFFF" } },
                fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "4FC15B" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            const mutuBuahHeaderStyle = {
                font: { bold: true, color: { rgb: "FFFFFF" } },
                fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "69349A" } },
                alignment: { horizontal: "center", vertical: "center" },
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            const cellBorderStyle = {
                border: {
                    top: { style: "thin", color: { rgb: "000000" } },
                    bottom: { style: "thin", color: { rgb: "000000" } },
                    left: { style: "thin", color: { rgb: "000000" } },
                    right: { style: "thin", color: { rgb: "000000" } }
                }
            };

            const centerAlignment = { alignment: { horizontal: "center", vertical: "center" } };
            const leftAlignment = { alignment: { horizontal: "left", vertical: "center" } };
            const rightAlignment = { alignment: { horizontal: "right", vertical: "center" } };

            for (const estateName in groupedByEstate) {
                const estateData = groupedByEstate[estateName];

                // Separate processing - no merging to preserve different supervisors
                const sheetData: any[] = [];

                // Process mutu_ancak data
                estateData.mutu_ancak.forEach(item => {
                    sheetData.push({
                        'TANGGAL INSPEKSI': item.inspection_date || '-',
                        'DIVISI': item.division ?? '-',
                        'BLOK': item.block || '-',
                        'NAMA SUPERVISOR': item.spv_name || '-',
                        'LEVEL SUPERVISOR': item.job_level || '-',
                        'JENIS INSPEKSI': 'Mutu Ancak',
                        'NOMOR POTONG': '-',
                        'NAMA PEMANEN': item.name || '-',
                        'NIP': item.nip ?? '-',
                        'JABATAN': item.job_title ?? '-',
                        'MANDOR PANEN': item.foreman_name ?? '-',
                        'KERANI PANEN': item.kerani_harvest_name ?? '-',
                        'MANDOR 1': item.foreman1_name ?? '-',
                        'ASISTEN': item.asisten_name ?? '-',
                        'ASISTEN KEPALA': item.asisten_kepala_name ?? '-',
                        'MANAGER': item.estate_manager ?? '-',
                        'KODE PENALTY': item.penalty_code ?? '-',
                        'DESKRIPSI PENALTY': item.penalty_descr ?? '-',
                        'NO BARIS': item.no_baris ?? '-',
                        'NO POKOK': item.no_pokok ?? '-',
                        'QTY': item.qty === 0 ? '-' : (item.qty ?? '-'),
                        'KETERANGAN TEMUAN MUTU ANCAK': item.ins_description ?? '-',
                        'NO TPH': '-',
                        'MENTAH (QTY - JJG)': '-',
                        'BUSUK/TANGKOS (QTY - JJG)': '-',
                        'TANGKAI PANJANG (QTY - JJG)': '-',
                        'KETERANGAN TEMUAN MUTU BUAH': '-',
                    });
                });

                // Process mutu_buah data
                estateData.mutu_buah.forEach(item => {
                    sheetData.push({
                        'TANGGAL INSPEKSI': item.inspection_date_mb || '-',
                        'DIVISI': item.division ?? '-',
                        'BLOK': item.block_mb || '-',
                        'NAMA SUPERVISOR': item.spv_name_mb || '-',
                        'LEVEL SUPERVISOR': item.job_level_mb || '-',
                        'JENIS INSPEKSI': 'Mutu Buah',
                        'NOMOR POTONG': item.no_potong ?? '-',
                        'NAMA PEMANEN': item.name || '-',
                        'NIP': item.nip ?? '-',
                        'JABATAN': item.job_title ?? '-',
                        'MANDOR PANEN': item.foreman_name ?? '-',
                        'KERANI PANEN': item.kerani_harvest_name ?? '-',
                        'MANDOR 1': item.foreman1_name ?? '-',
                        'ASISTEN': item.asisten_name ?? '-',
                        'ASISTEN KEPALA': item.asisten_kepala_name ?? '-',
                        'MANAGER': item.estate_manager ?? '-',
                        'KODE PENALTY': '-',
                        'DESKRIPSI PENALTY': '-',
                        'NO BARIS': '-',
                        'NO POKOK': '-',
                        'QTY': '-',
                        'KETERANGAN TEMUAN MUTU ANCAK': '-',
                        'NO TPH': item.tph_code || '-',
                        'MENTAH (QTY - JJG)': (item.unripe_qty === 0 || item.unripe_qty === null) ? '-' : item.unripe_qty,
                        'BUSUK/TANGKOS (QTY - JJG)': (item.rotten_qty === 0 || item.rotten_qty === null) ? '-' : item.rotten_qty,
                        'TANGKAI PANJANG (QTY - JJG)': (item.lstalk_qty === 0 || item.lstalk_qty === null) ? '-' : item.lstalk_qty,
                        'KETERANGAN TEMUAN MUTU BUAH': item.mb_description ?? '-',
                    });
                });

                // Sort by date first, then by name
                sheetData.sort((a, b) => {
                    // Sort by inspection date
                    if (a['TANGGAL INSPEKSI'] && b['TANGGAL INSPEKSI']) {
                        const dateA = a['TANGGAL INSPEKSI'] === '-' ? new Date(0) : new Date(a['TANGGAL INSPEKSI']);
                        const dateB = b['TANGGAL INSPEKSI'] === '-' ? new Date(0) : new Date(b['TANGGAL INSPEKSI']);
                        return dateA.getTime() - dateB.getTime();
                    }
                    // If dates are equal or missing, sort by name as secondary
                    if (a['NAMA PEMANEN'] < b['NAMA PEMANEN']) return -1;
                    if (a['NAMA PEMANEN'] > b['NAMA PEMANEN']) return 1;
                    return 0;
                });

                const ws = XLSX.utils.json_to_sheet([], { header: columnOrder });

                // Add merged header row for section titles
                // Row 1: Section headers
                const mutuAncakStartCol = mutuAncakColumnOrder.findIndex(col => col === 'KODE PENALTY');
                const mutuAncakRange = XLSX.utils.encode_range({
                    s: { r: 0, c: mutuAncakStartCol },
                    e: { r: 0, c: mutuAncakColumnOrder.length - 1 }
                });
                const mutuBuahRange = XLSX.utils.encode_range({
                    s: { r: 0, c: mutuAncakColumnOrder.length },
                    e: { r: 0, c: columnOrder.length - 1 }
                });

                ws['!merges'] ??= [];
                ws['!merges'].push(XLSX.utils.decode_range(mutuAncakRange));
                ws['!merges'].push(XLSX.utils.decode_range(mutuBuahRange));

                // Set section header values and styling
                ws[XLSX.utils.encode_cell({ r: 0, c: mutuAncakStartCol })] = {
                    v: 'HASIL INSPEKSI MUTU ANCAK',
                    s: mutuAncakHeaderStyle
                };
                ws[XLSX.utils.encode_cell({ r: 0, c: mutuAncakColumnOrder.length })] = {
                    v: 'HASIL INSPEKSI MUTU BUAH',
                    s: mutuBuahHeaderStyle
                };

                // Add data starting from row 3 (0-indexed row 2)
                XLSX.utils.sheet_add_json(ws, sheetData, { skipHeader: true, origin: 'A3' });

                // Styling Column Headers (Row 2)
                for (let C = 0; C < columnOrder.length; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: 1, c: C });
                    if (!ws[cellAddress]) ws[cellAddress] = { v: columnOrder[C] };
                    else ws[cellAddress].v = columnOrder[C];

                    // Use appropriate header style based on column section
                    const generalColumns = ['TANGGAL INSPEKSI', 'DIVISI', 'BLOK', 'NAMA SUPERVISOR', 'LEVEL SUPERVISOR', 'JENIS INSPEKSI', 'NOMOR POTONG', 'NAMA PEMANEN', 'NIP', 'JABATAN', 'MANDOR PANEN', 'KERANI PANEN', 'MANDOR 1', 'ASISTEN', 'ASISTEN KEPALA', 'MANAGER'];
                    const mutuAncakColumns = ['KODE PENALTY', 'DESKRIPSI PENALTY', 'NO BARIS', 'NO POKOK', 'QTY', 'KETERANGAN TEMUAN MUTU ANCAK'];

                    if (generalColumns.includes(columnOrder[C])) {
                        ws[cellAddress].s = generalHeaderStyle;
                    } else if (mutuAncakColumns.includes(columnOrder[C])) {
                        ws[cellAddress].s = mutuAncakHeaderStyle;
                    } else {
                        ws[cellAddress].s = mutuBuahHeaderStyle;
                    }
                }

                // Add section headers styling to row 1 cells that don't have merged headers
                for (let C = 0; C < mutuAncakStartCol; ++C) {
                    const cellAddress = XLSX.utils.encode_cell({ r: 0, c: C });
                    if (!ws[cellAddress]) ws[cellAddress] = { v: '' };
                    // Apply general header style to empty cells above general columns
                    ws[cellAddress].s = {
                        ...generalHeaderStyle,
                        fill: { type: "pattern", pattern: "solid", fgColor: { rgb: "FFFFFF" } }
                    };
                }

                // Styling Data Rows (starting from row 3, 0-indexed row 2)
                sheetData.forEach((rowData, rowIndex) => {
                    // Check if this row has mutu buah issues (for yellow highlighting)
                    const hasMutuBuahIssue = rowData['MENTAH (QTY - JJG)'] !== '-' ||
                        rowData['TANGKAI PANJANG (QTY - JJG)'] !== '-' ||
                        rowData['BUSUK/TANGKOS (QTY - JJG)'] !== '-';

                    columnOrder.forEach((colName, colIndex) => {
                        const cellAddress = XLSX.utils.encode_cell({ r: rowIndex + 2, c: colIndex });

                        // Ensure cell exists even if empty
                        if (!ws[cellAddress]) {
                            ws[cellAddress] = { v: rowData[colName] || '-' };
                        }

                        if (ws[cellAddress]) {
                            ws[cellAddress].s = { ...cellBorderStyle };

                            // Add yellow background for rows with mutu buah issues
                            if (hasMutuBuahIssue) {
                                ws[cellAddress].s.fill = {
                                    type: "pattern",
                                    pattern: "solid",
                                    fgColor: { rgb: "FFFF99" } // Light yellow
                                };
                            }

                            // Specific alignments
                            if (['NAMA PEMANEN', 'NIP', 'JABATAN', 'MANDOR PANEN', 'KERANI PANEN', 'MANDOR 1', 'ASISTEN', 'ASISTEN KEPALA', 'MANAGER', 'DIVISI', 'BLOK', 'NAMA SUPERVISOR', 'LEVEL SUPERVISOR', 'KODE PENALTY', 'DESKRIPSI PENALTY', 'KETERANGAN TEMUAN MUTU ANCAK', 'KETERANGAN TEMUAN MUTU BUAH', 'JENIS INSPEKSI'].includes(colName)) {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...leftAlignment };
                            } else if (['TANGGAL INSPEKSI', 'NO TPH', 'NO BARIS', 'NO POKOK'].includes(colName)) {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...centerAlignment };
                            } else if (['QTY', 'MENTAH (QTY - JJG)', 'TANGKAI PANJANG (QTY - JJG)', 'BUSUK/TANGKOS (QTY - JJG)', 'NOMOR POTONG'].includes(colName)) {
                                ws[cellAddress].s = { ...ws[cellAddress].s, ...rightAlignment };

                                // Format angka dan styling untuk nilai "-"
                                if (ws[cellAddress].v === '-') {
                                    ws[cellAddress].s.font = { italic: true, color: { rgb: "808080" } };
                                } else if (typeof ws[cellAddress].v === 'number') {
                                    ws[cellAddress].t = 'n';
                                    // Format NOMOR POTONG without decimal separator
                                    if (colName === 'NOMOR POTONG') {
                                        ws[cellAddress].z = '0';
                                    } else {
                                        ws[cellAddress].z = '#,##0';
                                    }
                                }
                            }

                            // Format tanggal
                            if (colName === 'TANGGAL INSPEKSI' && ws[cellAddress].v !== '-') {
                                ws[cellAddress].t = 's'; // Set as string to avoid date conversion issues
                                // Convert date format from YYYY-MM-DD to DD/MM/YYYY
                                const dateValue = ws[cellAddress].v;
                                if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
                                    const [year, month, day] = dateValue.split('-');
                                    ws[cellAddress].v = `${day}/${month}/${year}`;
                                }
                            }
                        }
                    });
                });

                // Ensure all cells have borders - additional pass to fix any missing borders
                for (let row = 0; row <= sheetData.length + 2; row++) {
                    for (let col = 0; col < columnOrder.length; col++) {
                        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
                        if (!ws[cellAddress]) {
                            let cellValue = '-';
                            if (row === 0) {
                                // Section headers row
                                const mutuAncakStartCol = mutuAncakColumnOrder.findIndex(col => col === 'KODE PENALTY');
                                if (col === mutuAncakStartCol) {
                                    cellValue = 'HASIL INSPEKSI MUTU ANCAK';
                                } else if (col === mutuAncakColumnOrder.length) {
                                    cellValue = 'HASIL INSPEKSI MUTU BUAH';
                                } else {
                                    cellValue = '';
                                }
                            } else if (row === 1) {
                                // Column headers row
                                cellValue = columnOrder[col];
                            }
                            ws[cellAddress] = { v: cellValue };
                        }
                        if (!ws[cellAddress].s) {
                            ws[cellAddress].s = {};
                        }
                        ws[cellAddress].s.border = {
                            top: { style: "thin", color: { rgb: "000000" } },
                            bottom: { style: "thin", color: { rgb: "000000" } },
                            left: { style: "thin", color: { rgb: "000000" } },
                            right: { style: "thin", color: { rgb: "000000" } }
                        };
                    }
                }

                // Column Widths
                ws['!cols'] = [
                    { wch: 15 }, // TANGGAL INSPEKSI
                    { wch: 20 }, // DIVISI
                    { wch: 15 }, // BLOK
                    { wch: 25 }, // NAMA SUPERVISOR
                    { wch: 20 }, // LEVEL SUPERVISOR
                    { wch: 20 }, // JENIS INSPEKSI
                    { wch: 15 }, // NOMOR POTONG
                    { wch: 25 }, // NAMA PEMANEN
                    { wch: 15 }, // NIP
                    { wch: 20 }, // JABATAN
                    { wch: 25 }, // MANDOR PANEN
                    { wch: 25 }, // KERANI PANEN
                    { wch: 25 }, // MANDOR 1
                    { wch: 25 }, // ASISTEN
                    { wch: 25 }, // ASISTEN KEPALA
                    { wch: 25 }, // MANAGER
                    { wch: 18 }, // KODE PENALTY
                    { wch: 50 }, // DESKRIPSI PENALTY
                    { wch: 15 }, // NO BARIS
                    { wch: 15 }, // NO POKOK
                    { wch: 8 },  // QTY
                    { wch: 40 }, // KETERANGAN TEMUAN MUTU ANCAK
                    { wch: 20 }, // NO TPH
                    { wch: 18 }, // MENTAH (QTY - JJG)
                    { wch: 20 }, // BUSUK/TANGKOS (QTY - JJG)
                    { wch: 18 }, // TANGKAI PANJANG (QTY - JJG)
                    { wch: 40 }, // KETERANGAN TEMUAN MUTU BUAH
                ];

                ws['!rows'] = [
                    { hpt: 25 }, // Section header row height
                    { hpt: 25 }  // Column header row height
                ];

                // Nama sheet yang aman
                const safeSheetName = estateName.replace(/[\\/*?[\]:]/g, "").substring(0, 30);
                XLSX.utils.book_append_sheet(wb, ws, safeSheetName);
            }

            const selectedMonthLabel = months.find(m => m.value === selectedMonth)?.label ?? selectedMonth;
            XLSX.writeFile(wb, `Laporan Supervisi-Mutu Buah ${selectedMonthLabel}.xlsx`);
            trackDownload('Laporan Mutu Ancak-Mutu Buah', { month: selectedMonthLabel });

            toast({
                title: 'Success',
                description: 'Laporan supervisi-mutu buah berhasil diekspor',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Error exporting spv-mb report:', error);
            let description = 'Gagal mengekspor laporan supervisi-mutu buah';

            if (isAxiosError(error) && error.response) {
                description = `Gagal mengekspor laporan: ${error.response.data?.message ?? error.message}`;
            } else if (error instanceof Error) {
                description = `Gagal mengekspor laporan: ${error.message}`;
            }

            toast({
                title: 'Error',
                description,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            throw error;
        }
    }
};

export default ReportMtAncakMtBuah;
